import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE } from '../Constants';

export interface ERPConnection {
  provider: string;
  status: string;
  company_name?: string;
  connected_at?: string;
  last_used_at?: string;
}

interface ERPConnectionsContextValue {
  connections: ERPConnection[];
  loading: boolean;
  fetchConnections: () => Promise<void>;
  connectOmie: (appKey: string, appSecret: string) => Promise<{ success: boolean; message: string; company_name?: string }>;
  initiateBlingOAuth: (clientId: string, clientSecret: string) => Promise<{ success: boolean; authorize_url?: string; message?: string }>;
  // connectPje: (oab: string, uf: string) => Promise<{ success: boolean; message: string; company_name?: string }>; // PJe disabled for now
  disconnectERP: (provider: string) => Promise<{ success: boolean; message: string }>;
}

const ERPConnectionsContext = createContext<ERPConnectionsContextValue | null>(null);

export function useERPConnections() {
  const ctx = useContext(ERPConnectionsContext);
  if (!ctx) {
    throw new Error('useERPConnections must be used within ERPConnectionsProvider');
  }
  return ctx;
}

export function ERPConnectionsProvider({ children }: { children: React.ReactNode }) {
  const { getAccessTokenSilently } = useAuth0();
  const [connections, setConnections] = useState<ERPConnection[]>([]);
  const [loading, setLoading] = useState(false);

  const getHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return {
      'Authorization': `Auth ${token}`,
      'Content-Type': 'application/json',
    };
  }, [getAccessTokenSilently]);

  const fetchConnections = useCallback(async () => {
    try {
      setLoading(true);
      const headers = await getHeaders();
      const resp = await fetch(`${API_BASE}/api/erp/connections`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        setConnections(data.connections || []);
      }
    } catch (err) {
      console.error('Failed to fetch ERP connections:', err);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  const connectOmie = useCallback(async (appKey: string, appSecret: string) => {
    try {
      const headers = await getHeaders();
      const resp = await fetch(`${API_BASE}/api/erp/connect/omie`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ app_key: appKey, app_secret: appSecret }),
      });
      const data = await resp.json();
      if (resp.ok) {
        await fetchConnections();
        return { success: true, message: data.message, company_name: data.company_name };
      }
      return { success: false, message: data.detail || 'Erro ao conectar Omie.' };
    } catch (err) {
      return { success: false, message: 'Erro de rede ao conectar Omie.' };
    }
  }, [getHeaders, fetchConnections]);

  const initiateBlingOAuth = useCallback(async (clientId: string, clientSecret: string) => {
    try {
      const headers = await getHeaders();
      const params = new URLSearchParams({ client_id: clientId, client_secret: clientSecret });
      const resp = await fetch(`${API_BASE}/api/erp/connect/bling/authorize?${params}`, { headers });
      const data = await resp.json();
      if (resp.ok) {
        return { success: true, authorize_url: data.authorize_url };
      }
      return { success: false, message: data.detail || 'Erro ao iniciar OAuth Bling.' };
    } catch (err) {
      return { success: false, message: 'Erro de rede ao conectar Bling.' };
    }
  }, [getHeaders]);

  // PJe connection — disabled for now
  // const connectPje = useCallback(async (oab: string, uf: string) => {
  //   try {
  //     const headers = await getHeaders();
  //     const resp = await fetch(`${API_BASE}/api/erp/connect/pje`, {
  //       method: 'POST',
  //       headers,
  //       body: JSON.stringify({ oab, uf }),
  //     });
  //     const data = await resp.json();
  //     if (resp.ok) {
  //       await fetchConnections();
  //       return { success: true, message: data.message, company_name: data.company_name };
  //     }
  //     return { success: false, message: data.detail || 'Erro ao conectar PJe.' };
  //   } catch (err) {
  //     return { success: false, message: 'Erro de rede ao conectar PJe.' };
  //   }
  // }, [getHeaders, fetchConnections]);

  const disconnectERP = useCallback(async (provider: string) => {
    try {
      const headers = await getHeaders();
      const resp = await fetch(`${API_BASE}/api/erp/disconnect/${provider}`, {
        method: 'DELETE',
        headers,
      });
      const data = await resp.json();
      if (resp.ok) {
        await fetchConnections();
        return { success: true, message: data.message };
      }
      return { success: false, message: data.detail || 'Erro ao desconectar.' };
    } catch (err) {
      return { success: false, message: 'Erro de rede ao desconectar.' };
    }
  }, [getHeaders, fetchConnections]);

  return (
    <ERPConnectionsContext.Provider value={{
      connections,
      loading,
      fetchConnections,
      connectOmie,
      initiateBlingOAuth,
      // connectPje, // PJe disabled for now
      disconnectERP,
    }}>
      {children}
    </ERPConnectionsContext.Provider>
  );
}
